import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiClientError,
  DecorKindSchema,
  type ApiClientErrorCode,
  type DecorKind,
  type DecorListItem,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { DecorsSearchView, type DecorsSearchState } from './DecorsSearchView';
import { useDecorImageUrls } from './useDecorImageUrls';
import {
  readDecorFilterParams,
  decorFilterToQuery,
  type DecorFilterParams,
} from './decorFilterParams';

/**
 * Container Экрана 2.2.2-search «Найти декор по названию» (ADR-0067). См. ADR-0005.
 *
 * Живёт на `/decors/:kind/search` — ветка «Найти декор по названию» метод-пикера
 * (2.2.2). Живой поиск по названию (подстрока name+brand, матчер бэка/MSW
 * `matchesDecorName`) с debounce 300мс и минимумом 2 символа.
 *
 * Активный фильтр (subcategory/brand/type) приходит из URL verbatim и
 * композится с `name` через AND (persistent intent, R3) — поиск идёт ВНУТРИ
 * выбранного фильтра, как фото/каталог (решение Aibek, ADR-0043/0067).
 *
 * `q` в URL — transient signal (R3): синхронизируется через `setSearchParams(...,
 * { replace: true })`, чтобы «Назад» НЕ копил историю по каждому нажатию клавиши
 * и вёл на предыдущий экран (метод-пикер, P-036). Фильтр-параметры при этом
 * сохраняются (functional updater преобразует только `q`).
 *
 * Cursor pagination «Показать ещё» — как DecorsCatalog: items накапливаются,
 * сброс при смене запроса/фильтра.
 */
const DEFAULT_LIMIT = 12;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

export function DecorsSearch() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string }>();
  const kindParse = DecorKindSchema.safeParse(params.kind);
  // Silent fallback при неизвестном kind — единый паттерн блока (DecorsCatalog,
  // MethodPicker, PhotoPicker используют тот же fallback на 'facade').
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';

  const [searchParams, setSearchParams] = useSearchParams();

  // Активный фильтр из URL (persistent intent, R3). filterKey — стабильный dep:
  // не меняется при апдейте только `q`, поэтому fetch не передёргивается зря.
  const filter = useMemo<DecorFilterParams>(
    () => readDecorFilterParams(searchParams),
    [searchParams],
  );
  const filterKey = JSON.stringify(filter);
  // Массивы, стабильные по filterKey (как DecorsCatalog appliedSubs/Brands/Types).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appliedSubs = useMemo(() => filter.subcategories, [filterKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appliedBrands = useMemo(() => filter.brands, [filterKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appliedTypes = useMemo(() => filter.types, [filterKey]);

  // Controlled-значение поля. Сидируется из `?q=` (deep-link / refresh / возврат).
  // Читаем один раз при маунте — далее input источник истины, URL ведомый.
  const [query, setQuery] = useState<string>(() => searchParams.get('q') ?? '');
  // Debounced-копия `query` (300мс) — драйвит и URL-sync, и fetch.
  const [debounced, setDebounced] = useState<string>(query);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Sync debounced → `?q=` (transient signal, replace:true). Functional updater
  // сохраняет фильтр-параметры; пустой запрос убирает `q` из URL.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const trimmed = debounced.trim();
        // Пишем trimmed (а не raw): поиск идёт по trimmedQuery, deep-link `?q=`
        // должен совпадать (без хвостовых пробелов `q=Дуб+`).
        if (trimmed === '') next.delete('q');
        else next.set('q', trimmed);
        return next;
      },
      { replace: true },
    );
  }, [debounced, setSearchParams]);

  const [items, setItems] = useState<readonly DecorListItem[]>([]);
  // Фото-2 (PR-DATA4): signed-URL для фото видимых карточек.
  const photoKeys = useMemo(() => items.map((i) => i.primaryPhotoKey), [items]);
  const { urls: imageUrls } = useDecorImageUrls(photoKeys);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);

  const trimmedQuery = debounced.trim();
  const tooShort = trimmedQuery.length < MIN_QUERY_LENGTH;

  useEffect(() => {
    if (tooShort) {
      setItems([]);
      setNextCursor(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setItems([]);
    setNextCursor(null);
    let cancelled = false;
    apiClient.decors
      .list(kind, {
        name: trimmedQuery,
        subcategories: [...appliedSubs],
        brands: [...appliedBrands],
        types: [...appliedTypes],
        limit: DEFAULT_LIMIT,
      })
      .then(
        (res) => {
          if (cancelled) return;
          setItems(res.items);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          if (cancelled) return;
          // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
          setLoading(false);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [kind, trimmedQuery, tooShort, filterKey, appliedSubs, appliedBrands, appliedTypes]);

  const onLoadMore = () => {
    if (!nextCursor || loading) return;
    setLoading(true);
    setError(null);
    apiClient.decors
      .list(kind, {
        name: trimmedQuery,
        subcategories: [...appliedSubs],
        brands: [...appliedBrands],
        types: [...appliedTypes],
        cursor: nextCursor,
        limit: DEFAULT_LIMIT,
      })
      .then(
        (res) => {
          setItems((prev) => [...prev, ...res.items]);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
          setLoading(false);
        },
      );
  };

  // 5 состояний (P-021). loading во время «Показать ещё» не переключает
  // success → loading (guard `items.length === 0`).
  const state = useMemo<DecorsSearchState>(() => {
    if (error) return 'error';
    if (loading && items.length === 0) return 'loading';
    if (tooShort) return 'idle';
    if (items.length === 0) return 'empty';
    return 'success';
  }, [error, loading, items.length, tooShort]);

  return (
    <DecorsSearchView
      kind={kind}
      state={state}
      query={query}
      onQueryChange={setQuery}
      items={items}
      nextCursor={nextCursor}
      error={error ?? undefined}
      imageUrls={imageUrls}
      onLoadMore={onLoadMore}
      onItemClick={(globalId) => {
        // ADR-0055: несём активный фильтр в URL карточки → дальше в альтернативы.
        // `q` (transient) НЕ протягиваем — карточка не часть поискового состояния.
        const qs = decorFilterToQuery(filter);
        navigate(
          `/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}${qs ? `?${qs}` : ''}`,
        );
      }}
    />
  );
}
